import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
    Loader2,
    Gavel,
    Send,
    CheckCircle2,
    ShieldAlert,
} from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { DISPUTE } from "../lib/testIds";
import {
    openDispute,
    fetchDisputeForRental,
    postDisputeMessage,
    resolveDispute,
} from "../lib/api";

const formatTime = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
};

const formatMoney = (n) =>
    (Number(n) || 0).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
    });

/**
 * DisputeThreadModal
 * - mode="open" (renter, no existing dispute) → shows compose form
 * - mode="view" (either party, dispute exists) → loads thread + lets them reply / resolve
 *
 * Props:
 *  - rental: { id, item_name, item_image, deposit, deposit_withheld_reason, return_photos, ... }
 *  - currentUserId: string (used to derive role)
 *  - open, onOpenChange
 *  - onChanged: callback(dispute) when a state change happens (opened / message / resolved)
 */
export const DisputeThreadModal = ({
    rental,
    currentUserId,
    open,
    onOpenChange,
    onChanged,
}) => {
    const [loading, setLoading] = useState(false);
    const [dispute, setDispute] = useState(null);
    const [composeText, setComposeText] = useState("");
    const [reply, setReply] = useState("");
    const [showResolve, setShowResolve] = useState(false);
    const [resolveNote, setResolveNote] = useState("");
    const [busy, setBusy] = useState(false);
    const listRef = useRef(null);

    useEffect(() => {
        if (!open || !rental) return;
        setComposeText("");
        setReply("");
        setShowResolve(false);
        setResolveNote("");
        setDispute(null);
        setLoading(true);
        fetchDisputeForRental(rental.id)
            .then((d) => setDispute(d))
            .catch((err) => {
                if (err?.response?.status !== 404) {
                    toast.error("Couldn't load the dispute thread");
                }
                setDispute(null);
            })
            .finally(() => setLoading(false));
    }, [open, rental]);

    useEffect(() => {
        if (listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollHeight;
        }
    }, [dispute?.messages?.length]);

    if (!rental) return null;

    const role =
        dispute && currentUserId
            ? dispute.renter_id === currentUserId
                ? "renter"
                : dispute.owner_id === currentUserId
                  ? "owner"
                  : null
            : null;
    const isResolved = dispute?.status === "resolved";
    const deposit = rental.deposit_amount || rental.deposit || dispute?.deposit_amount || 0;

    const handleOpen = async () => {
        const body = composeText.trim();
        if (!body) {
            toast.error("Add a message explaining your side");
            return;
        }
        try {
            setBusy(true);
            const created = await openDispute(rental.id, body);
            setDispute(created);
            setComposeText("");
            onChanged?.(created);
            toast.success("Dispute opened", {
                description: "We've notified the lender.",
            });
        } catch (err) {
            toast.error("Couldn't open the dispute", {
                description:
                    err?.response?.data?.detail || "Please try again in a moment.",
            });
        } finally {
            setBusy(false);
        }
    };

    const handleReply = async () => {
        const body = reply.trim();
        if (!body || !dispute) return;
        try {
            setBusy(true);
            const updated = await postDisputeMessage(dispute.id, body);
            setDispute(updated);
            setReply("");
            onChanged?.(updated);
        } catch (err) {
            toast.error("Couldn't send your message", {
                description:
                    err?.response?.data?.detail || "Please try again in a moment.",
            });
        } finally {
            setBusy(false);
        }
    };

    const handleResolve = async () => {
        if (!dispute) return;
        try {
            setBusy(true);
            const updated = await resolveDispute(dispute.id, resolveNote.trim());
            setDispute(updated);
            setShowResolve(false);
            setResolveNote("");
            onChanged?.(updated);
            toast.success("Dispute resolved");
        } catch (err) {
            toast.error("Couldn't resolve the dispute", {
                description:
                    err?.response?.data?.detail || "Please try again in a moment.",
            });
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid={DISPUTE.modal}
                className="max-h-[90vh] max-w-xl overflow-y-auto rounded-3xl border border-[#2e3220]/10 bg-[#F4F1EA] p-7 text-[#2e3220]"
            >
                <DialogTitle className="flex items-center gap-2 font-display text-2xl font-bold text-[#2e3220]">
                    <Gavel className="h-6 w-6 text-[#B56C5A]" strokeWidth={2.5} />
                    Deposit dispute
                </DialogTitle>
                <DialogDescription className="text-sm text-[#5a6142]">
                    {rental.item_name} · Deposit at stake: {formatMoney(deposit)}
                </DialogDescription>

                {/* Withheld reason snapshot */}
                {(rental.deposit_withheld_reason ||
                    dispute?.withheld_reason) && (
                    <div className="mt-4 rounded-2xl border border-[#B56C5A]/30 bg-[#B56C5A]/10 p-3">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-[#B56C5A]">
                            Lender&apos;s withhold reason
                        </p>
                        <p className="mt-1 text-xs italic text-[#7a3a30]">
                            “{rental.deposit_withheld_reason ||
                                dispute?.withheld_reason}”
                        </p>
                    </div>
                )}

                {loading ? (
                    <div className="mt-6 flex items-center justify-center py-10">
                        <Loader2 className="h-5 w-5 animate-spin text-[#737a3d]" />
                    </div>
                ) : !dispute ? (
                    /* Renter opening flow */
                    <div className="mt-5">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                            Your side of the story
                        </label>
                        <textarea
                            data-testid={DISPUTE.openMessage}
                            value={composeText}
                            onChange={(e) =>
                                setComposeText(e.target.value.slice(0, 2000))
                            }
                            rows={5}
                            placeholder="Explain what happened and reference the return photos. Stay factual and respectful — your lender will see this immediately."
                            className="mt-2 w-full resize-none rounded-2xl border border-[#2e3220]/15 bg-white/70 px-4 py-3 text-sm text-[#2e3220] placeholder:text-[#5a6142]/60 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#B56C5A]"
                        />
                        <p className="mt-1 text-right text-[10px] text-[#5a6142]/70">
                            {composeText.length}/2000
                        </p>
                        <div className="mt-4 flex items-center justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => onOpenChange(false)}
                                disabled={busy}
                                className="rounded-full border border-[#2e3220]/15 bg-transparent px-5 py-2 text-sm font-semibold text-[#2e3220] hover:bg-[#2e3220]/5 disabled:opacity-60"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                data-testid={DISPUTE.openSubmit}
                                onClick={handleOpen}
                                disabled={busy || !composeText.trim()}
                                className="inline-flex items-center gap-2 rounded-full bg-[#B56C5A] px-5 py-2 text-sm font-bold uppercase tracking-widest text-[#F4F1EA] hover:bg-[#9d5b4c] disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                                <ShieldAlert className="h-4 w-4" strokeWidth={2.5} />
                                Open dispute
                            </button>
                        </div>
                    </div>
                ) : (
                    /* Existing thread view */
                    <>
                        <div
                            ref={listRef}
                            data-testid={DISPUTE.threadList}
                            className="mt-5 max-h-72 overflow-y-auto rounded-2xl border border-[#2e3220]/10 bg-white/50 p-3"
                        >
                            <ul className="space-y-3">
                                {dispute.messages.map((m, i) => {
                                    const mine = m.user_id === currentUserId;
                                    return (
                                        <li
                                            key={m.id}
                                            data-testid={DISPUTE.threadMessage(i)}
                                            className={`flex ${mine ? "justify-end" : "justify-start"}`}
                                        >
                                            <div
                                                className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${
                                                    mine
                                                        ? "bg-[#737a3d] text-[#F4F1EA]"
                                                        : "bg-[#2e3220]/5 text-[#2e3220]"
                                                }`}
                                            >
                                                <p
                                                    className={`text-[10px] font-bold uppercase tracking-widest ${mine ? "text-[#F4F1EA]/70" : "text-[#737a3d]"}`}
                                                >
                                                    {m.user_name} · {m.role} ·{" "}
                                                    {formatTime(m.created_at)}
                                                </p>
                                                <p className="mt-1 whitespace-pre-wrap leading-snug">
                                                    {m.body}
                                                </p>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>

                        {isResolved ? (
                            <div className="mt-4 rounded-2xl border border-[#737a3d]/30 bg-[#737a3d]/10 px-3 py-3">
                                <p className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-[#5f6534]">
                                    <CheckCircle2 className="h-3 w-3" strokeWidth={2.5} />
                                    Resolved · {formatTime(dispute.resolved_at)}
                                </p>
                                {dispute.resolution_note && (
                                    <p className="mt-1 text-xs italic text-[#5a6142]">
                                        “{dispute.resolution_note}”
                                    </p>
                                )}
                            </div>
                        ) : showResolve ? (
                            <div className="mt-4 rounded-2xl border border-[#737a3d]/30 bg-[#737a3d]/5 p-3">
                                <label className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                                    Resolution note (optional)
                                </label>
                                <textarea
                                    data-testid={DISPUTE.resolveNote}
                                    value={resolveNote}
                                    onChange={(e) =>
                                        setResolveNote(
                                            e.target.value.slice(0, 1000),
                                        )
                                    }
                                    rows={3}
                                    placeholder="e.g. Agreed to refund $15 via Venmo — closing here."
                                    className="mt-2 w-full resize-none rounded-2xl border border-[#2e3220]/15 bg-white/70 px-4 py-2 text-sm text-[#2e3220] placeholder:text-[#5a6142]/60 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                                />
                                <div className="mt-2 flex items-center justify-end gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setShowResolve(false)}
                                        disabled={busy}
                                        className="rounded-full border border-[#2e3220]/15 bg-transparent px-3 py-1.5 text-xs font-semibold text-[#2e3220] hover:bg-[#2e3220]/5 disabled:opacity-60"
                                    >
                                        Back
                                    </button>
                                    <button
                                        type="button"
                                        data-testid={DISPUTE.resolveSubmit}
                                        onClick={handleResolve}
                                        disabled={busy}
                                        className="inline-flex items-center gap-1.5 rounded-full bg-[#737a3d] px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-[#F4F1EA] hover:bg-[#5f6534] disabled:opacity-60"
                                    >
                                        {busy && (
                                            <Loader2 className="h-3 w-3 animate-spin" />
                                        )}
                                        <CheckCircle2 className="h-3 w-3" strokeWidth={2.5} />
                                        Mark resolved
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="mt-4">
                                <div className="flex items-end gap-2">
                                    <textarea
                                        data-testid={DISPUTE.reply}
                                        value={reply}
                                        onChange={(e) =>
                                            setReply(e.target.value.slice(0, 2000))
                                        }
                                        rows={2}
                                        placeholder={`Reply as ${role || "you"}…`}
                                        className="flex-1 resize-none rounded-2xl border border-[#2e3220]/15 bg-white/70 px-4 py-2 text-sm text-[#2e3220] placeholder:text-[#5a6142]/60 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                                    />
                                    <button
                                        type="button"
                                        data-testid={DISPUTE.replySubmit}
                                        onClick={handleReply}
                                        disabled={busy || !reply.trim()}
                                        className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#737a3d] text-[#F4F1EA] hover:bg-[#5f6534] disabled:cursor-not-allowed disabled:opacity-60"
                                        aria-label="Send"
                                    >
                                        {busy ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                            <Send className="h-4 w-4" strokeWidth={2.5} />
                                        )}
                                    </button>
                                </div>
                                <div className="mt-3 flex justify-end">
                                    <button
                                        type="button"
                                        data-testid={DISPUTE.resolveBtn}
                                        onClick={() => setShowResolve(true)}
                                        disabled={busy}
                                        className="inline-flex items-center gap-1.5 rounded-full border border-[#737a3d]/40 bg-transparent px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#737a3d] hover:bg-[#737a3d]/5 disabled:opacity-60"
                                    >
                                        <CheckCircle2 className="h-3 w-3" strokeWidth={2.5} />
                                        Mark resolved
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default DisputeThreadModal;
